(() => {
  if (customElements.get('shipping-cost-widget')) return;

  const cache = new Map();
  const successTtl = 5 * 60 * 1000;
  const query = `
    mutation ShippingRate($input: CartInput!, $country: CountryCode!) @inContext(country: $country) {
      cartCreate(input: $input) {
        cart {
          id
          lines(first: 2) {
            nodes {
              quantity
              merchandise { ... on ProductVariant { id } }
            }
          }
          deliveryGroups(first: 10) {
            nodes {
              deliveryOptions {
                deliveryMethodType
                estimatedCost {
                  amount
                  currencyCode
                }
              }
            }
          }
        }
        userErrors {
          field
          message
          code
        }
        warnings {
          code
          message
        }
      }
    }
  `;

  const readQuery = `
    query ShippingRateRead($id: ID!, $country: CountryCode!) @inContext(country: $country) {
      cart(id: $id) {
        lines(first: 2) {
          nodes {
            quantity
            merchandise { ... on ProductVariant { id } }
          }
        }
        deliveryGroups(first: 10) {
          nodes {
            deliveryOptions {
              deliveryMethodType
              estimatedCost { amount currencyCode }
            }
          }
        }
      }
    }
  `;

  function pause(ms, signal) {
    return new Promise((resolve, reject) => {
      const stop = () => {
        window.clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      };
      const timer = window.setTimeout(() => {
        signal.removeEventListener('abort', stop);
        resolve();
      }, ms);
      if (signal.aborted) stop();
      else signal.addEventListener('abort', stop, { once: true });
    });
  }

  class ShippingCostWidget extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.hasEnteredViewport = false;
      this.intersectionObserver = null;
      this.productInfo = null;
      this.variantId = '';
      this.variantChangeTimer = null;
      this.handleCountrySelection = this.handleCountrySelection.bind(this);
      this.handleProductChange = this.handleProductChange.bind(this);
      this.handleIntersection = this.handleIntersection.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.handleCountrySelection, true);
      this.productInfo = this.closest('product-info');
      this.productInfo?.addEventListener('change', this.handleProductChange);

      const initialVariant = {
        id: this.dataset.variantId,
        requiresShipping: this.dataset.requiresShipping === 'true',
      };
      this.setVariant(initialVariant);

      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver(this.handleIntersection, {
          rootMargin: '200px 0px',
          threshold: 0,
        });
        this.intersectionObserver.observe(this);
      } else {
        this.hasEnteredViewport = true;
        this.calculate();
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleCountrySelection, true);
      this.productInfo?.removeEventListener('change', this.handleProductChange);
      this.intersectionObserver?.disconnect();
      this.abortController?.abort();
      window.clearTimeout(this.variantChangeTimer);
    }

    handleCountrySelection(event) {
      const countryOption = event.target.closest?.('a[data-value]');
      if (!countryOption || !this.contains(countryOption) || countryOption.lang) return;

      const countryCode = countryOption.dataset.value;
      const form = countryOption.closest('form');
      const countryInput = form?.querySelector('input[name="country_code"]');
      if (!form || !countryInput || !/^[A-Z]{2}$/.test(countryCode)) return;

      const returnUrl = new URL(window.location.href);
      returnUrl.searchParams.set('country', countryCode);
      let returnToInput = form.querySelector('input[name="return_to"]');
      if (!returnToInput) {
        returnToInput = document.createElement('input');
        returnToInput.type = 'hidden';
        returnToInput.name = 'return_to';
        form.appendChild(returnToInput);
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      countryInput.value = countryCode;
      returnToInput.value = `${returnUrl.pathname}${returnUrl.search}${returnUrl.hash}`;
      form.submit();
    }

    handleIntersection(entries) {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.hasEnteredViewport = true;
      this.intersectionObserver?.disconnect();
      this.calculate();
    }

    handleProductChange(event) {
      if (!event.target.matches('input[name="id"]')) return;
      if (event.target.closest('product-info') !== this.productInfo) return;

      window.clearTimeout(this.variantChangeTimer);
      this.variantChangeTimer = window.setTimeout(() => {
        const variant = this.readSelectedVariant(event.target.value);
        this.setVariant(variant);
      }, 100);
    }

    readSelectedVariant(fallbackId) {
      const variantJson = this.productInfo?.querySelector('variant-selects [data-selected-variant]');

      if (variantJson?.textContent) {
        try {
          const variant = JSON.parse(variantJson.textContent);
          return {
            id: String(variant.id || fallbackId || ''),
            requiresShipping: variant.requires_shipping !== false,
          };
        } catch (_error) {
          return { id: String(fallbackId || ''), requiresShipping: true };
        }
      }

      return { id: String(fallbackId || ''), requiresShipping: this.dataset.requiresShipping === 'true' };
    }

    setVariant(variant) {
      const nextVariantId = String(variant?.id || '');
      const requiresShipping = Boolean(variant?.requiresShipping && nextVariantId);

      this.abortController?.abort();
      this.variantId = nextVariantId;
      this.dataset.variantId = nextVariantId;
      this.dataset.requiresShipping = String(requiresShipping);
      this.hidden = !requiresShipping;

      if (!requiresShipping) return;

      this.renderLoading();
      if (this.hasEnteredViewport) this.calculate();
    }

    cacheKey() {
      return `${this.variantId}:${this.dataset.countryCode}`;
    }

    getCachedResult() {
      const key = this.cacheKey();
      const cached = cache.get(key);
      if (!cached) return null;
      if (cached.expiresAt <= Date.now()) {
        cache.delete(key);
        return null;
      }
      return cached.result;
    }

    cacheResult(result) {
      if (result.kind === 'fallback') return;
      cache.set(this.cacheKey(), {
        expiresAt: Date.now() + successTtl,
        result,
      });
    }

    async calculate() {
      if (this.hidden || !this.variantId || !this.hasEnteredViewport) return;

      const cached = this.getCachedResult();
      if (cached) {
        this.renderResult(cached);
        return;
      }

      this.abortController?.abort();
      this.abortController = new AbortController();
      const controller = this.abortController;
      const requestedVariant = this.variantId;
      const requestedKey = this.cacheKey();
      let timedOut = false;
      const deadline = window.setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, 18000);
      this.renderLoading();

      try {
        let payload = await this.requestRates(query, {
          country: this.dataset.countryCode,
          input: {
            buyerIdentity: { countryCode: this.dataset.countryCode },
            delivery: {
              addresses: [
                {
                  address: {
                    deliveryAddress: { countryCode: this.dataset.countryCode },
                  },
                  oneTimeUse: true,
                  selected: true,
                  validationStrategy: 'COUNTRY_CODE_ONLY',
                },
              ],
            },
            lines: [
              {
                merchandiseId: `gid://shopify/ProductVariant/${requestedVariant}`,
                quantity: 1,
              },
            ],
          },
        }, controller.signal);
        let result = this.parseResult(payload, requestedVariant);
        const cartId = payload?.data?.cartCreate?.cart?.id;
        if (result.kind === 'fallback' && cartId && this.validCart(payload, requestedVariant)) {
          for (const delay of [400, 1200]) {
            await pause(delay, controller.signal);
            payload = await this.requestRates(readQuery, {
              id: cartId,
              country: this.dataset.countryCode,
            }, controller.signal);
            result = this.parseResult(payload, requestedVariant);
            if (result.kind !== 'fallback' || !this.validCart(payload, requestedVariant)) break;
          }
        }
        if (controller.signal.aborted || requestedKey !== this.cacheKey() || controller !== this.abortController) return;
        this.cacheResult(result);
        this.renderResult(result);
      } catch (error) {
        if ((!timedOut && error.name === 'AbortError') || requestedKey !== this.cacheKey() || controller !== this.abortController) return;
        this.renderResult({ kind: 'fallback' });
      } finally {
        window.clearTimeout(deadline);
      }
    }

    async requestRates(operation, variables, signal) {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const response = await fetch(this.dataset.endpoint, {
            method: 'POST',
            credentials: 'omit',
            headers: { 'Content-Type': 'application/json' },
            signal,
            body: JSON.stringify({ query: operation, variables }),
          });
          if (!response.ok) {
            const error = new Error(String(response.status));
            error.retryable = response.status === 408 || response.status === 429 || (response.status >= 500 && response.status <= 599);
            throw error;
          }
          return await response.json();
        } catch (error) {
          if (signal.aborted || attempt > 0 || !(error instanceof TypeError || error.retryable)) throw error;
          await pause(600, signal);
        }
      }
    }

    validCart(payload, requestedVariant) {
      const created = payload?.data?.cartCreate;
      const cart = created ? created.cart : payload?.data?.cart;
      if (payload?.errors?.length || !cart || created?.userErrors?.length || created?.warnings?.length) return false;
      const lines = cart.lines?.nodes || [];
      return lines.length === 1 && lines[0].quantity === 1 &&
        lines[0].merchandise?.id === `gid://shopify/ProductVariant/${requestedVariant}`;
    }

    parseResult(payload, requestedVariant = this.variantId) {
      if (!this.validCart(payload, requestedVariant)) return { kind: 'fallback' };
      const cart = payload.data.cartCreate?.cart || payload.data.cart;
      const groups = cart.deliveryGroups?.nodes || [];
      if (groups.length !== 1) return { kind: 'fallback' };
      const options = groups
        .flatMap((group) => group.deliveryOptions || [])
        .filter((option) => option.deliveryMethodType === 'SHIPPING' && option.estimatedCost);

      if (!options.length) return { kind: 'fallback' };

      const currencies = new Set(options.map((option) => option.estimatedCost.currencyCode));
      if (currencies.size !== 1) {
        return { kind: 'fallback' };
      }

      const amounts = options.map((option) => Number(option.estimatedCost.amount));
      if (amounts.some((amount) => !Number.isFinite(amount) || amount < 0)) return { kind: 'fallback' };

      const cheapest = Math.min(...amounts);
      if (cheapest === 0) return { kind: 'free' };

      return {
        kind: options.length > 1 ? 'multiple' : 'single',
        amount: cheapest,
        currency: [...currencies][0],
      };
    }

    formatAmount(result) {
      const number = new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(result.amount);
      const parts = new Intl.NumberFormat(this.dataset.locale, {
        style: 'currency',
        currency: result.currency,
        currencyDisplay: 'narrowSymbol',
      }).formatToParts(result.amount);
      const symbol = parts.find((part) => part.type === 'currency')?.value || result.currency;
      return `${number}${symbol}`;
    }

    renderLoading() {
      this.dataset.state = 'loading';
      this.querySelector('[data-shipping-cost-message]').textContent = this.dataset.loadingText;
      this.querySelector('[data-shipping-cost-message]').setAttribute('aria-busy', 'true');
    }

    renderResult(result) {
      let message = this.dataset.fallbackText;

      if (result.kind === 'free') {
        message = this.dataset.freeTemplate;
      } else if (result.kind === 'single' || result.kind === 'multiple') {
        try {
          const template = result.kind === 'single' ? this.dataset.singleTemplate : this.dataset.multipleTemplate;
          message = template.replace('__AMOUNT__', this.formatAmount(result));
        } catch (_error) {
          message = this.dataset.fallbackText;
        }
      }

      message = message.replace('__COUNTRY__', this.dataset.countryName);
      this.dataset.state = result.kind;
      this.querySelector('[data-shipping-cost-message]').textContent = message;
      this.querySelector('[data-shipping-cost-message]').setAttribute('aria-busy', 'false');
    }
  }

  customElements.define('shipping-cost-widget', ShippingCostWidget);
})();
